import React, { useMemo } from "react";
import { Table, Button, Tag, Switch, Space, Popconfirm, Typography, Empty, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { POSITION_GROUPS, positionLabel } from "../../utils/positionMeta";

const { Title } = Typography;

const MAX_VISIBLE_POSITIONS = 2;

const adMatchesPosition = (ad, position) =>
  Array.isArray(ad.positions) &&
  (ad.positions.includes(position) || ad.positions.includes("*"));

const buildColumns = ({ onEdit, onDelete, onToggleStatus }) => [
  // PositionsOverview.jsx — inside buildColumns

{
  title: "Ad",
  dataIndex: "name",
  key: "name",
  width: 320,
  render: (_, ad) => (
    <Space align="center" style={{ maxWidth: 300, overflow: "hidden" }}>
      <img
        src={ad.image}
        alt={ad.name}
        style={{
          width: 40,
          height: 40,
          minWidth: 40,
          minHeight: 40,
          maxWidth: 40,
          maxHeight: 40,
          objectFit: "cover",
          borderRadius: 4,
          flexShrink: 0,
          backgroundColor: "#f5f5f5",
        }}
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
      <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
        <div
          style={{
            fontWeight: 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={ad.name}
        >
          {ad.name}
        </div>
        <div style={{ fontSize: 12, color: "#888" }}>{ad.organization}</div>
      </div>
    </Space>
  ),
},
  {
    title: "Positions",
    dataIndex: "positions",
    key: "positions",
    width: 220,
    render: (positions = []) => {
      const visible = positions.slice(0, MAX_VISIBLE_POSITIONS);
      const overflow = positions.slice(MAX_VISIBLE_POSITIONS);

      return (
        <Space size={4} wrap style={{ maxWidth: 210 }}>
          {visible.map((p) => (
            <Tag key={p} color={p === "*" ? "purple" : "blue"}>
              {positionLabel(p)}
            </Tag>
          ))}
          {overflow.length > 0 && (
            <Tooltip title={overflow.map(positionLabel).join(", ")}>
              <Tag>+{overflow.length}</Tag>
            </Tooltip>
          )}
        </Space>
      );
    },
  },
  {
    title: "Paid",
    dataIndex: "paid",
    key: "paid",
    width: 80,
    render: (paid) => <Tag color={paid ? "gold" : "default"}>{paid ? "Paid" : "Free"}</Tag>,
  },
  {
    title: "Weight",
    dataIndex: "weight",
    key: "weight",
    width: 80,
  },
  {
    title: "Schedule",
    key: "schedule",
    width: 200,
    render: (_, ad) => (
      <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
        {ad.durationFrom || "—"} → {ad.durationTo || "No end date"}
      </span>
    ),
  },
  {
    title: "Status",
    key: "status",
    width: 90,
    render: (_, ad) => (
      <Switch
        size="small"
        checked={ad.status === "ACTIVE"}
        onChange={(checked) => onToggleStatus(ad, checked)}
        checkedChildren="ON"
        unCheckedChildren="OFF"
      />
    ),
  },
  {
    title: "",
    key: "actions",
    width: 100,
    align: "right",
    render: (_, ad) => (
      <Space>
        <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(ad)} />
        <Popconfirm
          title="Delete this ad?"
          description="It can be restored later from the Deleted tab."
          onConfirm={() => onDelete(ad)}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    ),
  },
];

const PositionsOverview = ({ ads, onAddForPosition, onEdit, onDelete, onToggleStatus }) => {
  const columns = useMemo(
    () => buildColumns({ onEdit, onDelete, onToggleStatus }),
    [onEdit, onDelete, onToggleStatus]
  );

  return (
    <div style={{ marginBottom: 40 }}>
      {POSITION_GROUPS.map(({ group, positions }) => (
        <div key={group} style={{ marginBottom: 32 }}>
          <Title level={5} style={{ marginBottom: 12 }}>
            {group}
          </Title>

          {positions.map(({ value, label }, index) => {
            const positionAds = (ads || []).filter((ad) => adMatchesPosition(ad, value));

            return (
              <div key={value} style={{ marginBottom: 20 }}>
                <div
                  style={{
                    display: "flex",
                    justifySpace: "between",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontWeight: 500 }}>{label}</span>
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => onAddForPosition(value)}
                  >
                    Add Ad
                  </Button>
                </div>

                {positionAds.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No ads assigned"
                    style={{ margin: "12px 0" }}
                  />
                ) : (
                  <Table
                    size="small"
                    rowKey="id"
                    dataSource={positionAds}
                    columns={columns}
                    pagination={false}
                    tableLayout="fixed"
                    showHeader={index === 0} // Hides header for all positions after the first one in the group
                  />
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default PositionsOverview;